// Session-only cache. Redux continues to hold the active project's arrays.
export const EMPTY_PROJECT = Object.freeze({
  tasks: [], notes: [], phases: [], modules: [], error: "",
});

export function createProjectCache(db, { maxProjects = 5, keepAliveMs = 60000 } = {}) {
  const entries = new Map();
  const keyOf = (spaceId, stationId) => JSON.stringify([spaceId, stationId]);

  const stop = (entry) => {
    clearTimeout(entry.timer);
    entry.timer = null;
    entry.version += 1;
    entry.stops.forEach((unsubscribe) => unsubscribe());
    entry.stops = [];
  };
  const publish = (entry, patch) => {
    entry.data = { ...entry.data, ...patch };
    entry.listeners.forEach((notify) => notify());
  };
  const trim = () => {
    for (const [key, entry] of entries) {
      if (entries.size <= maxProjects) break;
      if (!entry.listeners.size) {
        stop(entry);
        entries.delete(key);
      }
    }
  };
  const start = (entry, spaceId, stationId) => {
    const version = ++entry.version;
    const ref = db.collection("space").doc(spaceId).collection("stations").doc(stationId);
    if (entry.data.error) publish(entry, { error: "" });
    const listen = (query, field, read) => {
      let waitingForServer = entry.loaded.has(field);
      let received = false;
      return query.onSnapshot(
        { includeMetadataChanges: true },
        (snap) => {
          if (entry.version !== version) return;
          // A reconnect's local snapshot may be incomplete. Keep our saved data
          // until the server confirms the result, including an actually empty one.
          if (waitingForServer && snap.metadata.fromCache) return;
          waitingForServer = false;
          if (received && snap.docChanges && !snap.docChanges().length) return;
          received = true;
          entry.loaded.add(field);
          publish(entry, { [field]: read(snap) });
        },
        (error) => {
          if (entry.version !== version) return;
          stop(entry);
          publish(entry, { ...EMPTY_PROJECT, error: "Some project content could not be loaded. Check your access or reopen the project to retry." });
          console.error("Project subscription failed:", error);
        },
      );
    };
    const records = (snap) => snap.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
    entry.stops = [
      listen(ref.collection("phases"), "phases", records),
      listen(ref.collection("tasks"), "tasks", records),
      listen(ref.collection("notes").orderBy("createdAt", "desc"), "notes", records),
      listen(ref.collection("modules").doc("modules"), "modules", (snap) => {
        const modules = snap.exists ? snap.data().modules : null;
        return Array.isArray(modules) ? modules : [];
      }),
    ];
  };

  return {
    getSnapshot(spaceId, stationId) {
      return entries.get(keyOf(spaceId, stationId))?.data || EMPTY_PROJECT;
    },
    subscribe(spaceId, stationId, notify) {
      const key = keyOf(spaceId, stationId);
      const entry = entries.get(key) || {
        data: EMPTY_PROJECT, loaded: new Set(), listeners: new Set(), stops: [], timer: null, version: 0,
      };
      // Map order tracks most recently visited projects, not background updates.
      entries.delete(key);
      entries.set(key, entry);
      clearTimeout(entry.timer);
      entry.timer = null;
      entry.listeners.add(notify);
      if (!entry.stops.length) start(entry, spaceId, stationId);
      trim();
      return () => {
        entry.listeners.delete(notify);
        if (entries.get(key) !== entry) return;
        if (!entry.listeners.size) {
          if (keepAliveMs > 0) entry.timer = setTimeout(() => stop(entry), keepAliveMs);
          else stop(entry);
          trim();
        }
      };
    },
    forget(spaceId, stationId) {
      const key = keyOf(spaceId, stationId);
      const entry = entries.get(key);
      if (!entry) return;
      stop(entry);
      publish(entry, EMPTY_PROJECT);
      if (!entry.listeners.size) entries.delete(key);
    },
    dispose() {
      entries.forEach(stop);
      entries.clear();
    },
  };
}
